import { Context, Effect, Layer, Stream } from 'effect'
import type { DocumentViewerSourceDefinition } from '../document-viewer-types'
import { type DocumentRendererService } from '../document-viewer-services'
import type { ResolvedPageStructure, ViewerPage } from '../models'

export type HostPageRenderPage = {
  pageNumber: number
  width: number
  height: number
}

export type HostPageRenderSourceInput = {
  pages: HostPageRenderPage[]
}

export class HostDocumentRenderer extends Context.Service<
  HostDocumentRenderer,
  DocumentRendererService<unknown, ViewerPage>
>()('HostDocumentRenderer') {}

function toResolvedPages(input: HostPageRenderSourceInput): ResolvedPageStructure[] {
  return input.pages.map((page) => {
    return {
      pageNumber: page.pageNumber,
      bounds: { x: 0, y: 0, width: page.width, height: page.height },
      textRects: [],
      links: []
    } satisfies ResolvedPageStructure
  })
}

/**
 * Creates a host-rendered source definition for DocumentViewer.
 *
 * Use this when your app renders page content itself in `renderPage(...)`
 * and DVK only needs page layout metadata (page number + dimensions).
 */
export const HostDocumentRendererLive = Layer.sync(HostDocumentRenderer, () => {
  let nextSessionId = 1
  const service: DocumentRendererService<HostPageRenderSourceInput, ResolvedPageStructure> = {
    open: (input) =>
      Effect.sync(() => {
        const sessionId = `host-layout-session:${nextSessionId}`
        nextSessionId += 1
        const pages = toResolvedPages(input)
        return {
          sessionId,
          pages,
          renderMode: 'layoutOnly' as const
        }
      }),
    close: () => Effect.void,
    setDesiredPages: () => Effect.succeed(undefined),
    events: () => Effect.succeed(Stream.empty),
    attachPageSurface: () => Effect.void,
    detachPageSurface: () => Effect.void,
    releasePageText: () => Effect.succeed(undefined)
  }

  return service as DocumentRendererService<unknown, ViewerPage>
})

export function createHostPageRenderSourceDefinition(): DocumentViewerSourceDefinition {
  return {
    rendererTag: HostDocumentRenderer
  }
}
